import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getOrderssWithPagination } from '@app/services/order';
import { getAllActiveFranchise } from '@app/services/franchise';
import Select from 'react-select';
import moment from 'moment';
import { base64Encode } from '@app/helpers/base64url';

const ORDER_STATUSES = [
    { label: 'Sent to Print Partner', value: 'sent' },
    { label: 'In Production', value: 'acknowledged' },
    { label: 'Dispatched', value: 'dispatched' },
];

const STATUS_BADGE: Record<string, string> = {
    sent: 'badge-warning',
    acknowledged: 'badge-info',
    dispatched: 'badge-success',
    unpaid: 'badge-secondary',
    failed: 'badge-danger',
    cancelled: 'badge-dark',
};

const PrintOperationsList = () => {
    const canAccess = useSelector((state: any) => state.auth.permissions?.['print-operations']);
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [franchises, setFranchises] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const [search, setSearch] = useState({
        franchiseId: '',
        invoiceNumber: '',
        totalSets: '',
        totalHandbooks: '',
        orderTotal: '',
        printerOrderNumber: '',
        orderStatus: ['sent', 'acknowledged'],
        paymentSource: '',
    });

    const customStyle: TableStyles = {
        head: { style: { color: '#5E5873', fontSize: '13px', fontWeight: 900 } },
        headCells: { style: { borderColor: '#E9ECEF', textTransform: 'uppercase', letterSpacing: '1px' } },
        headRow: { style: { backgroundColor: '#F7F7F8', minHeight: '44px', fontWeight: 500 } },
    };

    useEffect(() => {
        if (canAccess?.['list-orders'] === false) {
            toast.error('You do not have permission to access this page');
            navigate('/');
        }
    }, []);

    useEffect(() => {
        fetchOrders(1, perPage, sortField, sortDirection);
    }, [search]);

    useEffect(() => {
        fetchFranchises();
    }, []);

    const fetchOrders = async (page, pp, sf, sd) => {
        try {
            setLoading(true);
            const resp = await getOrderssWithPagination(page, pp, sf, sd, search);
            if (resp?.success === true) {
                setOrders(resp.collections.data);
                setTotalRows(resp.collections.pagination.total);
            } else {
                toast.error(resp?.errors?.[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        } finally {
            setLoading(false);
        }
    };

    const fetchFranchises = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) setFranchises(resp.collections);
        } catch {}
    };

    const columns = [
        {
            name: '#ID',
            selector: (row: any) => `#${row.id}`,
            sortable: true,
            sortField: 'id',
            width: '80px',
        },
        {
            name: 'Printer No',
            selector: (row: any) => row.printer_order_no ?? '---',
            sortable: true,
            sortField: 'printer_order_no',
            width: '110px',
        },
        {
            name: 'Franchise',
            selector: (row: any) => row.franchise_name,
            sortable: true,
            sortField: 'franchise_name',
            wrap: true,
        },
        {
            name: 'Sets Qty',
            selector: (row: any) => row.qty_sets ?? 0,
            sortable: true,
            sortField: 'qty_sets',
            width: '100px',
        },
        {
            name: 'Handbooks Qty',
            selector: (row: any) => row.qty_handbooks ?? 0,
            sortable: true,
            sortField: 'qty_handbooks',
            width: '130px',
        },
        {
            name: 'Order Date',
            selector: (row: any) => row.created_at,
            format: (row: any) => row.created_at ? moment(row.created_at).format('DD/MM/YYYY') : '---',
            sortable: true,
            sortField: 'created_at',
            width: '120px',
        },
        {
            name: 'Status',
            cell: (row: any) => (
                <span className={`badge ${STATUS_BADGE[row.order_status] ?? 'badge-secondary'}`}>
                    {ORDER_STATUSES.find(s => s.value === row.order_status)?.label ?? row.order_status}
                </span>
            ),
            sortable: true,
            sortField: 'order_status',
            width: '160px',
        },
        {
            name: 'Action',
            cell: (row: any) => (
                canAccess?.['print-panel'] && (
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(`/print-operations/print/${base64Encode(row.id.toString())}`)}
                        title={row.order_status === 'dispatched' ? 'View Order' : 'Print Order'}
                    >
                        <i className={`fas ${row.order_status === 'dispatched' ? 'fa-eye' : 'fa-print'}`} />
                    </button>
                )
            ),
            width: '80px',
            center: true,
        },
    ];

    const handleReset = () => {
        setSearch({
            franchiseId: '',
            invoiceNumber: '',
            totalSets: '',
            totalHandbooks: '',
            orderTotal: '',
            printerOrderNumber: '',
            orderStatus: ['sent', 'acknowledged'],
            paymentSource: '',
        });
    };

    return (
        <div>
            <section className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6"><h1>Print Operations</h1></div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item active">Print Operations</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Orders for Printing</h3>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3 align-items-end">
                                <div className="col-sm-3 mb-2">
                                    <Select
                                        placeholder="Filter by Franchise"
                                        options={franchises.map((f: any) => ({ value: f.id, label: f.name }))}
                                        onChange={(opt: any) => setSearch({ ...search, franchiseId: opt ? opt.value : '' })}
                                        value={franchises.map((f: any) => ({ value: f.id, label: f.name })).find((o: any) => o.value == search.franchiseId) || null}
                                        isClearable
                                    />
                                </div>
                                <div className="col-sm-2 mb-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Printer Order No"
                                        value={search.printerOrderNumber}
                                        onChange={(e) => setSearch({ ...search, printerOrderNumber: e.target.value })}
                                    />
                                </div>
                                <div className="col-sm-3 mb-2">
                                    <Select
                                        isMulti
                                        placeholder="Filter by Status"
                                        options={ORDER_STATUSES}
                                        onChange={(opts: any) => setSearch({ ...search, orderStatus: opts ? opts.map((o: any) => o.value) : [] })}
                                        value={ORDER_STATUSES.filter(s => search.orderStatus.includes(s.value))}
                                        closeMenuOnSelect={false}
                                        styles={{
                                            control: (base: any) => ({
                                                ...base,
                                                minHeight: '38px',
                                            }),
                                            valueContainer: (base: any) => ({
                                                ...base,
                                                padding: '2px 8px',
                                            }),
                                            multiValue: (base: any) => ({
                                                ...base,
                                                margin: '2px',
                                            }),
                                            multiValueLabel: (base: any) => ({
                                                ...base,
                                                fontSize: '12px',
                                                padding: '2px 6px',
                                            }),
                                            multiValueRemove: (base: any) => ({
                                                ...base,
                                                padding: '0 4px',
                                            }),
                                        }}
                                    />
                                </div>
                                <div className="col-sm-2 mb-2">
                                    <Button isLoading={isLoading} onClick={handleReset} className="btn btn-warning">Reset</Button>
                                </div>
                            </div>

                            <DataTable
                                columns={columns}
                                data={orders}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                paginationDefaultPage={currentPage}
                                onChangePage={(page) => { fetchOrders(page, perPage, sortField, sortDirection); setCurrentPage(page); }}
                                onChangeRowsPerPage={(pp, page) => { fetchOrders(page, pp, sortField, sortDirection); setPerPage(pp); }}
                                paginationPerPage={perPage}
                                paginationRowsPerPageOptions={[5, 10, 20, 50]}
                                progressPending={isLoading}
                                sortServer
                                onSort={(col, dir) => { setSortField(col.sortField); setSortDirection(dir); fetchOrders(currentPage, perPage, col.sortField, dir); }}
                                customStyles={customStyle}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrintOperationsList;
